/**
 * Cart types — v3 schema.
 *
 * Identity model: a `CartLine` represents one bookable thing — one
 * (experience × package × date × time × traveler-mix). Two adds with the same
 * (experience, package, date, time) merge into the same line; different
 * combos create new lines.
 *
 * The store is the source of truth at runtime. localStorage persists it.
 * `priceSnapshot` is for display only; the server's `/api/quote` is
 * authoritative for cart totals and checkout submission.
 */

export type TravelersState = Record<string, number>;

/** Every member here is re-derived from the server on each validation pass and
 *  persisted in the cart cookie, so a status must be a fact that stays true
 *  until the server says otherwise.
 *
 *  `stale_price` is INFORMATIONAL — the refresh failed, the snapshot stands,
 *  and checkout must stay open. Only `unavailable` and `min_pax_unmet` block.
 *
 *  There is deliberately NO `price_updated` member. A price change is an
 *  ANNOUNCEMENT, not a state: it is true once, at the moment it is applied,
 *  and false forever after. Persisting it pinned the banner to the cookie —
 *  the traveller kept reading "Price increased: was X, now Y" days later about
 *  a change already applied, and on a rolling deploy an older instance saw a
 *  status it could neither render nor clear and blocked the CTA with no reason
 *  shown. The announcement now lives in component state
 *  (`cart/page.tsx` `priceNotices`), where it cannot outlive the mount. */
export type CartLineStatus =
  | "valid"
  | "stale_price"
  | "unavailable"
  | "min_pax_unmet";

export interface PriceSnapshot {
  total: number;
  currency: string;
  capturedAt: string;
}

export interface CartLine {
  id: string;
  experienceId: string;
  experienceSlug: string;
  experienceName: string;
  experienceImageUrl: string;
  packageId: string;
  packageName: string;
  date: string;
  time: string | null;
  /** The package's real start times in canonical 24-hour `HH:MM` (the only
   *  values the quote/booking API accepts). The cart's reschedule popover
   *  offers exactly these — never fabricated 12-hour slots. Empty/absent for
   *  date-only packages (no time picker). */
  availableTimes?: string[];
  travelers: TravelersState;
  categoryLabels: Record<string, string>;
  /** Seats ONE count of each category consumes: person → 1, per-unit/per-group
   *  → unitCapacity (Cabin = 4), flat group fee → 0. Lets the cart compute
   *  head-count (Σ count × seatsPerUnit) for its capacity check so it agrees
   *  with the booking widget and the server's seat math instead of counting
   *  rows. Optional — carts persisted before this field default each to 1. */
  categorySeatsPerUnit?: Record<string, number>;
  priceSnapshot: PriceSnapshot;
  status: CartLineStatus;
  statusMessage?: string;
  addedAt: string;
  /**
   * ISO timestamp when this line's reservation expires. Set to
   * `now + CART_HOLD_MINUTES` on insert and refreshed on every user-driven
   * update (matches GYG/Viator: interacting with the cart extends the hold).
   * Status flips to `unavailable` once the deadline passes.
   */
  reservedUntil: string;
}

export type CartLineDraft = Omit<
  CartLine,
  "id" | "addedAt" | "status" | "statusMessage" | "reservedUntil"
>;

/**
 * GYG/Viator both hold cart reservations for ~60 minutes from last
 * interaction. We mirror that on the client today; Phase 6 enforces it
 * server-side.
 */
export const CART_HOLD_MINUTES = 60;

export const CART_SCHEMA_VERSION = 6 as const;
// Derive the storage/broadcast keys from the schema version so the suffix can
// never drift out of lockstep with the version gate (a `_v5` key under schema
// `6` silently wipes carts on deploy — P0-1).
export const CART_STORAGE_KEY =
  `tripcart_cart_v${CART_SCHEMA_VERSION}` as const;
export const CART_BROADCAST_CHANNEL = CART_STORAGE_KEY;
export const LEGACY_CART_STORAGE_KEY =
  `tripcart_cart_v${CART_SCHEMA_VERSION - 1}` as const;

/**
 * Server-rendered cart shape. The source of truth is a row in traveler-api's
 * `carts` table, keyed by an opaque token; the cookie carries only that token
 * (signed, httpOnly), not this shape — so there is no cap on line count tied
 * to the ~4 KB browser cookie limit the way there used to be.
 */
export interface Cart {
  lines: CartLine[];
  /** ISO timestamp of last server-side mutation. */
  updatedAt: string;
  /** Schema version — evolve the cookie payload safely. */
  version: typeof CART_SCHEMA_VERSION;
}

export const EMPTY_CART: Cart = {
  lines: [],
  updatedAt: new Date(0).toISOString(),
  version: CART_SCHEMA_VERSION,
};

/** Cookie name for the signed cart token. */
export const CART_COOKIE_NAME = "tc_cart";

/**
 * Cookie holding the one line a "Book Now" is buying.
 *
 * A second cookie rather than a field on the cart, for two reasons that both
 * matter. The lifetimes stay independent. And deleting one physically cannot
 * touch the other — which is the property the old behaviour lacked: Book Now
 * wrote into the cart, so checking out cleared trips the traveller never
 * agreed to buy.
 *
 * The shape is a whole `Cart` holding a single line, so everything downstream —
 * pricing, quotes, the order summary — reads the same structure either way and
 * needs no idea which basket it was handed.
 *
 * This is the shape GetYourGuide's Partner API settled on: a booking created
 * without a `shopping_cart_hash` "automatically creates a new cart", isolated
 * from whatever else the traveller had going.
 */
export const DIRECT_COOKIE_NAME = "tc_direct";
